/**
 * Predicates for filtering out known, unactionable noise in Datadog RUM.
 *
 * Each predicate identifies a specific source of browser-side errors that we
 * have deliberately chosen not to surface — typically because the error does
 * not originate from our code, lacks a usable stack trace, or is produced by
 * automated scanners/bots hitting our pages.
 *
 * Add a new predicate here when you identify another distinct noise pattern.
 * Keep predicates narrow and exact-shape; broad matches risk swallowing real
 * user errors.
 */

/**
 * Headless Chrome is the user-agent used by most link-preview scanners,
 * synthetic monitors, and scrapers. These sessions generate RUM events that
 * are never tied to a real user journey and only inflate noise.
 */
export const isHeadlessBot = (): boolean => navigator.userAgent.includes('HeadlessChrome')

/**
 * Cross-origin `"Script error."` is the browser's scrubbed error message when
 * a script from another origin throws without CORS headers. No stack, no file,
 * no line — completely unactionable.
 *
 * With `crossOrigin: 'anonymous'` in next.config.ts our own scripts report full
 * details; only third-party scripts we can't control produce this.
 *
 * The browser-rum SDK wraps non-Error throws with an `Uncaught ` prefix and
 * JSON.stringifies the raw string, producing `Uncaught "Script error."` — so
 * we match both the raw form and the SDK-wrapped form.
 */
export const isCrossOriginScriptError = (message: string | undefined): boolean =>
    message === 'Script error.' || (message?.startsWith('Uncaught "Script error.') ?? false)

/**
 * Microsoft Outlook Safe Links / Office link-preview scanners inject a script
 * into a hidden frame that calls `.update()` on an ActiveX-style host object
 * which only exists inside the Office desktop environment. On a normal web
 * page the object is missing, so Microsoft's own script throws and our origin
 * catches it.
 *
 * Always the exact shape:
 *     `Object Not Found Matching Id:<n>, MethodName:update, ParamCount:4`
 *
 * Volume correlates with email blasts, never with real users.
 */
export const isOutlookSafeLinksScanner = (message: string | undefined): boolean =>
    message?.includes('Object Not Found Matching Id:') ?? false

/**
 * Curated list of Google Identity Services (One Tap / FedCM) console messages we
 * have confirmed are benign, user-driven noise we cannot fix from our code: the
 * user dismisses the One Tap prompt, the browser aborts or declines the FedCM
 * request, the token fetch is cancelled, or a third-party-cookie / FedCM policy
 * blocks the flow.
 *
 * Messages matching this list are dropped outright (see client.ts beforeSend) —
 * they are confirmed-unuseful, so there is no reason to keep them. This is an
 * explicit allowlist of *known-unuseful* patterns, NOT a blanket `[GSI_LOGGER]`
 * match. An unrecognized GSI error is deliberately left at error level so it
 * still surfaces — e.g. `The given origin is not allowed for the given client
 * ID` is a real OAuth-client misconfiguration and is intentionally absent here.
 * The tradeoff is that a genuinely-new noise variant pages until we add it;
 * that's preferred over silently masking an unknown, possibly-real error. Add a
 * pattern only after confirming it's noise. Substrings, matched anywhere in the
 * message.
 */
const THIRD_PARTY_AUTH_NOISE_PATTERNS = [
    // Google — every FedCM get() rejection (AbortError "signal is aborted",
    // NetworkError "Error retrieving a token", "publicKey is not defined",
    // NotAllowedError "identity-credentials-get … not enabled", …).
    'FedCM get() rejects',
    // Google — credential-status polling that returns nothing usable.
    'Check credential status returns invalid response',
    // Google — failed XHRs to the GSI endpoint (no logger prefix).
    'accounts.google.com/gsi/',
]

export const isThirdPartyAuthNoise = (message: string | undefined): boolean => {
    if (!message) {
        return false
    }
    return THIRD_PARTY_AUTH_NOISE_PATTERNS.some((pattern) => message.includes(pattern))
}

/**
 * The Amplitude browser SDK logs a `console.error` for every analytics request
 * it can't complete — `Amplitude Logger [Error]: Load failed` (Safari) or
 * `Amplitude Logger [Error]: Failed to fetch` (Chromium). These are
 * overwhelmingly ad/tracker blockers blocking the first-party `an.wanderu.com`
 * (events) and `an-config.wanderu.com` (remote-config) beacons: fire-and-forget,
 * not actionable from our code, and the dominant client log-error source. The
 * paired status-0 network log is dropped by host (see client.ts beforeSend);
 * this drops the SDK's own console line.
 *
 * Narrow on purpose: only the two blocked-fetch phrasings, and only when carrying
 * the SDK's `Amplitude Logger` tag — so genuine SDK errors (e.g. `No remote
 * config received`, init failures) still surface at error.
 */
export const isAmplitudeFetchNoise = (message: string | undefined): boolean => {
    if (!message?.startsWith('Amplitude Logger')) {
        return false
    }
    return message.includes('Load failed') || message.includes('Failed to fetch')
}

/**
 * Next.js App Router fires RSC prefetch / navigation GETs carrying a `?_rsc=`
 * marker. When the user hovers or navigates away, Next aborts the in-flight
 * prefetch and the fetch reports `status_code: 0` — expected cancellation, not a
 * failure. A genuine RSC failure returns a real 4xx/5xx status and still surfaces.
 * Volume spikes with traffic surges (e.g. email blasts) as prefetches are started
 * and cancelled en masse. Narrow: status 0 + GET + the `_rsc=` marker only.
 */
export const isAbortedRscPrefetch = (
    method: string | undefined,
    status: number | undefined,
    url: string | undefined,
): boolean => status === 0 && method === 'GET' && (url?.includes('_rsc=') ?? false)

/**
 * The search-widget autocomplete fires a debounced GET to
 * `/api/nexus/places/autocomplete` on each keystroke. When the user keeps typing,
 * submits the search, or navigates away, the browser drops the in-flight request
 * and the Datadog logs SDK's fetch instrumentation reports it as `status_code: 0`
 * ("Fetch error GET …") — an expected cancellation, not a failure, and by far the
 * single largest client error-log source (see the aborted-RSC-prefetch case above;
 * same class, different path). A genuine backend failure returns a real 4xx/5xx
 * status and still surfaces — and the route also logs it server-side. Narrow:
 * status 0 + GET + the autocomplete path only.
 */
export const isAbortedAutocompleteRequest = (
    method: string | undefined,
    status: number | undefined,
    url: string | undefined,
): boolean =>
    status === 0 && method === 'GET' && (url?.includes('/api/nexus/places/autocomplete') ?? false)

/**
 * Hosts whose every request is third-party ad/measurement traffic. Matched against
 * the parsed hostname as an exact match or a dot-boundary suffix, so `doubleclick.net`
 * covers `ad.`, `stats.g.` and `googleads.g.`, and `analytics.google.com` covers both
 * the bare host and GA4's region-prefixed forms (`region1.…`).
 */
const AD_TECH_BEACON_HOSTS = [
    'doubleclick.net',
    'googleadservices.com',
    'analytics.google.com',
    'google-analytics.com',
]

/**
 * Collector paths on hosts that also serve non-beacon traffic, so the host alone is
 * too broad a match. Google's search domains carry the Ads conversion (`ccm`),
 * remarketing (`rmkt`), measurement and `pagead` collectors alongside everything else
 * google.com serves.
 */
const GOOGLE_ADS_COLLECTOR_PATH_PREFIXES = ['/ccm/', '/rmkt/', '/measurement/', '/pagead/']

/** `google.com`, `www.google.de`, `www.google.co.uk` — the tag fires at the visitor's TLD. */
const GOOGLE_SEARCH_DOMAIN = /^(www\.)?google(\.[a-z]{2,3}){1,2}$/

/**
 * GTM's own telemetry ping. Deliberately *not* the whole googletagmanager.com host:
 * a failed `gtm.js` there would mean the container itself never loaded, which is
 * actionable. (Our loader comes from the first-party sGTM endpoint — see
 * `services/gtm/gtm-script.tsx` — so such a failure would be doubly anomalous.)
 */
const GTM_TELEMETRY_HOST = 'www.googletagmanager.com'
const GTM_TELEMETRY_PATH_PREFIX = '/td'

const matchesHost = (hostname: string, host: string): boolean =>
    hostname === host || hostname.endsWith(`.${host}`)

/**
 * Third-party ad/measurement beacons that never completed (`status_code: 0`) —
 * the GTM container's Google Ads conversion, remarketing and GA4 pings.
 *
 * Two populations produce these, neither actionable from our code:
 *  - **Declared crawlers.** AI and search crawlers execute the page's JS, fire the
 *    GTM tags, and then refuse or fail the ad-host requests.
 *  - **Ad/tracker blockers.** Extensions, Safari ITP, and privacy-first browsers
 *    (DuckDuckGo, Brave) block Google ad hosts by default.
 *
 * Matched on the **parsed hostname** — and, where the host also serves non-beacon
 * traffic, the path — never on the raw URL string and never on the log message or the
 * requesting user agent:
 *  - A **substring** match on the raw URL would let a first-party request smuggle a
 *    drop through a query parameter (`/api/nexus/schedules?ref=https://ad.doubleclick.net/x`).
 *  - A **message** match would swallow first-party failures wholesale — a run of
 *    `/api/nexus/schedules` 502s buried under this noise is what [#879] cost, and
 *    dropping by message would make that permanent.
 *  - A **crawler user-agent** match would discard the 5xx responses we serve *to*
 *    crawlers, which are among the most actionable errors we log (a 5xx served to a
 *    search or AI crawler is an indexing problem), while host-scoping already covers
 *    the crawler beacon noise.
 *
 * Narrow on purpose: only `status_code: 0` (the browser reporting "never
 * completed"). A real 4xx/5xx from the same host — a broken tag, a Google-side
 * outage — still reports.
 *
 * [#879]: https://github.com/wanderu/nexus/issues/879
 */
export const isFailedAdTechBeacon = (
    status: number | undefined,
    url: string | undefined,
): boolean => {
    if (status !== 0 || !url) {
        return false
    }

    let hostname: string
    let pathname: string
    try {
        ;({ hostname, pathname } = new URL(url))
    } catch {
        // Not an absolute URL — nothing to attribute to a third-party host, so keep it.
        return false
    }

    if (AD_TECH_BEACON_HOSTS.some((host) => matchesHost(hostname, host))) {
        return true
    }

    if (
        GOOGLE_SEARCH_DOMAIN.test(hostname) &&
        GOOGLE_ADS_COLLECTOR_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))
    ) {
        return true
    }

    return hostname === GTM_TELEMETRY_HOST && pathname.startsWith(GTM_TELEMETRY_PATH_PREFIX)
}

/**
 * True when an error's stack contains a frame served from one of our own origins:
 *  - `wanderu.com` — prod page (`www.wanderu.com`) and bundles (`static.wanderu.com`)
 *  - `wanderu.dev` — dev/pilot pages and bundles (`static.{dev,pilot}.wanderu.dev`)
 *
 * (Local dev serves chunks from `localhost` and ships no RUM/logs, so this hook
 * never runs there — no need to match it.)
 *
 * Used as a safety guard before silencing "third-party" noise: if our code is
 * anywhere in the call stack we keep the error, so we only ever drop errors we
 * are confident did not originate with us.
 */
const FIRST_PARTY_HOST_FRAGMENTS = ['wanderu.com', 'wanderu.dev']

const hasFirstPartyFrame = (stack: string | undefined): boolean =>
    stack != null && FIRST_PARTY_HOST_FRAGMENTS.some((host) => stack.includes(host))

/**
 * Browser-extension and in-app-webview chatter that runs in the page context but
 * originates entirely in code we don't own:
 *  - `runtime.sendMessage(). Tab not found` — a Chrome extension messaging its own
 *    background page after the tab/port went away.
 *  - `window.solana` / `window.phantom` — the Phantom crypto-wallet extension
 *    injecting a global and colliding.
 *  - `WKWebView API client did not respond` / `__bridge_callback is not defined` —
 *    an iOS in-app webview's host bridge (the page opened inside another app).
 *
 * These are dropped, but ONLY when the stack has no first-party frame — a matching
 * signature whose stack touches our bundle is kept at error (see hasFirstPartyFrame),
 * so a genuine regression in our code that happens to resemble one of these can't be
 * silently masked. Extension/webview errors typically carry an extension-origin stack
 * or none at all; an absent stack is treated as non-first-party and dropped. This is
 * a curated allowlist of confirmed-external signatures, NOT a blanket match —
 * ambiguous shapes that could be ours (e.g. `Unexpected token '{'`) are deliberately
 * absent and keep surfacing.
 */
const BROWSER_EXTENSION_NOISE_PATTERNS = [
    'runtime.sendMessage',
    'window.solana',
    'window.phantom',
    'WKWebView API client did not respond',
    '__bridge_callback',
]

export const isBrowserExtensionNoise = (
    message: string | undefined,
    stack: string | undefined,
): boolean => {
    if (!message) {
        return false
    }
    if (!BROWSER_EXTENSION_NOISE_PATTERNS.some((pattern) => message.includes(pattern))) {
        return false
    }
    return !hasFirstPartyFrame(stack)
}

/**
 * The location of each frame in a stack as the Datadog SDK hands it to `beforeSend`,
 * one frame per line in the form `  at <func> @ <url>:<line>:<col>`. The first line is
 * the error's name and message, and carries no frame.
 */
const stackFrameLocations = (stack: string | undefined): string[] =>
    (stack ?? '')
        .split('\n')
        .slice(1)
        .flatMap((line) => {
            const separator = line.indexOf(' @ ')
            return line.trimStart().startsWith('at ') && separator !== -1
                ? [line.slice(separator + 3).trim()]
                : []
        })

/**
 * A native frame (`[native code]`) is the browser's own built-in and belongs to whoever
 * called it, so it neither attributes a stack nor disqualifies one.
 */
const NATIVE_FRAME_LOCATION = '[native code]'

/**
 * True when the stack has at least one frame and every non-native frame's location
 * satisfies `isFrom`. A stack with no frames, or a frame with no URL (`@ undefined`),
 * cannot be attributed, so it never matches — an error we cannot place stays at error.
 */
const everyFrameIsFrom = (stack: string | undefined, isFrom: (location: string) => boolean) => {
    const locations = stackFrameLocations(stack).filter(
        (location) => location !== NATIVE_FRAME_LOCATION,
    )
    return locations.length > 0 && locations.every(isFrom)
}

/**
 * A stack whose every frame is served from a Chromium browser extension
 * (`chrome-extension://`, which Edge and other Chromium browsers share) — code the
 * visitor installed, which never ships from us. Attributed by where the code runs from
 * rather than by message, so it covers extensions we have never seen (wallets, coupon
 * and grammar tools). Any frame from elsewhere, ours included, keeps the error.
 *
 * Firefox (`moz-extension://`) and Safari (`safari-web-extension://`) extension frames
 * never reach this check: the SDK's stack parser discards them, so such a stack arrives
 * with no frames and is kept as unattributable.
 */
export const isExtensionOnlyStack = (stack: string | undefined): boolean =>
    everyFrameIsFrom(stack, (location) => location.startsWith('chrome-extension://'))

/**
 * A stack whose every frame is `<anonymous>` — script evaluated with no URL, which is
 * how a scraper's injected `page.evaluate`, an in-app browser's script, and a GTM Custom
 * HTML tag's async callback all report. The first two are not ours and the third is, so
 * a match must never be dropped outright.
 *
 * Any frame with a URL keeps the error. Our GTM container loads from the sGTM host, so a
 * tag it calls synchronously carries a `gtm.js` caller frame and never matches. A Custom
 * HTML tag's own async callback cannot be told apart from injected script: GTM minifies
 * Custom HTML when it compiles the container and strips comments, so a `//# sourceURL`
 * label never reaches the browser.
 */
export const isAnonymousOnlyStack = (stack: string | undefined): boolean =>
    everyFrameIsFrom(stack, (location) => location.startsWith('<anonymous>'))

/**
 * The Amplitude SDK logs a `console.error` when its Web Worker (payload compression
 * or the track-destination sender) fails to spawn — but the same line reports that
 * it is `falling back` to the main thread and continues. Self-healing informational
 * noise mislabeled as an error. Narrow: the SDK tag plus the `falling back` phrase.
 */
export const isAmplitudeWorkerFallbackNoise = (message: string | undefined): boolean =>
    (message?.startsWith('Amplitude Logger') ?? false) && message!.includes('falling back')

/**
 * Amplitude delivery that degraded but is expected under ad/tracker blocking:
 *  - `No remote config received` — the startup remote-config fetch came back empty
 *    (the `an-config.wanderu.com` beacon was blocked; a real server-side outage would
 *    spike across all users at once rather than trickle).
 *  - `exceeded retry count` — a fire-and-forget event was dropped after its retries.
 *
 * Kept as a faint heartbeat rather than dropped: these are downgraded to `warn` (see
 * client.ts beforeSend) so a genuine remote-config or delivery outage is still visible
 * on dashboards without paging on routine blocker noise.
 */
export const isAmplitudeDegradedDelivery = (message: string | undefined): boolean => {
    if (!message?.startsWith('Amplitude Logger')) {
        return false
    }
    return message.includes('No remote config received') || message.includes('exceeded retry count')
}

/**
 * `ResizeObserver loop completed with undelivered notifications.` (and the older
 * `ResizeObserver loop limit exceeded`) is a benign browser-generated signal — the
 * observer callback triggered a resize that couldn't be delivered before the next
 * paint. It carries no usable stack and needs no action; it is on every major site's
 * ignore list. Not dropped outright — downgraded to `warn` (see client.ts beforeSend)
 * so a genuine layout-thrash regression is still faintly visible without paging.
 */
export const isResizeObserverLoopNoise = (message: string | undefined): boolean =>
    message?.includes('ResizeObserver loop') ?? false
